import { useMemo } from 'react'

import { useQuery } from '@apollo/client'
import { PayrollRunFiltersOptionsDocument } from 'generated/resources/graphql'

type FilterOption = { value: string; label: string }
type FilterOptions = FilterOption[]

type CyclesListFilterOptions = {
  countryFilterOptions: FilterOptions
  currencyFilterOptions: FilterOptions
  loading: boolean
}

/**
 * Provides filter options for cycles list based on visible compensation cycles.
 *
 * This hook fetches the required data using a query and processes it to generate
 * unique country and currency filter options. The results include country and currency
 * options, as well as a loading state to track the query's progress.
 *
 * @return {CyclesListFilterOptions} An object containing the following properties:
 * - `countryFilterOptions`: An array of filter options for countries, where each option
 *   is represented by an object with `value` and `label` properties.
 * - `currencyFilterOptions`: An array of filter options for currencies, where each
 *   option is represented by an object with `value` and `label` properties.
 * - `loading`: A boolean indicating whether the query is still in progress.
 */
function useCyclesListFilterOptions(): CyclesListFilterOptions {
  // Filter options are now computed from visible compensation cycles (not hidden).
  // This ensures that filter options only show countries and currencies that have
  // active cycles, improving the user experience by not showing irrelevant filters.
  const { data: payrollRunFiltersOptions, loading } = useQuery(PayrollRunFiltersOptionsDocument, {
    variables: { includeHidden: false },
  })

  const { countryFilterOptions, currencyFilterOptions } = useMemo(() => {
    if (!payrollRunFiltersOptions?.compensations.cyclesConnection.nodes.length) {
      return { countryFilterOptions: [], currencyFilterOptions: [] }
    }

    const uniqueCountryOptions = new Set<string>()
    const countryOptions: FilterOptions = []
    const uniqueCurrencyOptions = new Set<string>()
    const currencyOptions: FilterOptions = []

    payrollRunFiltersOptions.compensations.cyclesConnection.nodes.forEach((cycle) => {
      // Add country filter option if valid and not already added
      if (cycle.country && cycle.countryLabel && !uniqueCountryOptions.has(cycle.country)) {
        uniqueCountryOptions.add(cycle.country)
        countryOptions.push({
          value: cycle.country,
          label: cycle.countryLabel,
        })
      }

      // Add currency filter option if valid and not already added
      if (cycle.currency && !uniqueCurrencyOptions.has(cycle.currency)) {
        uniqueCurrencyOptions.add(cycle.currency)
        currencyOptions.push({
          value: cycle.currency,
          label: cycle.currency,
        })
      }
    })

    return {
      countryFilterOptions: countryOptions,
      currencyFilterOptions: currencyOptions,
    }
  }, [payrollRunFiltersOptions])

  return {
    countryFilterOptions,
    currencyFilterOptions,
    loading,
  }
}

export { useCyclesListFilterOptions }
export type { CyclesListFilterOptions }
